import cx from 'classnames';
import { createContext, useContext, useMemo } from 'react';
import type { FunctionComponent } from 'react';

import { defineSlotComponent, findAllForSlot, findFirstForSlot } from '../../layouts/slots';
import Flex from '../Flex';
import PaidFeatureGate from '../Gates/PaidFeatureGate';
import RouterLink from '../Link/RouterLink';
import PaidPill from '../Pill/PaidPill';
import type { FossaFeature } from '../../settings/billing/features/consts';

import styles from './SidebarNav.module.less';

const SidebarNavContextActiveKey = createContext<string>('');
const useSidebarNavActiveKey = () => useContext(SidebarNavContextActiveKey);

//  This is a simple hook that will return the last path segment of a given pathname, if it exists.
function useActiveKey(pathname?: string): string {
  return useMemo(() => pathname?.split('/').pop() ?? '', [pathname]);
}

type SidebarNavItemProps = {
  /**
   * Where the link should navigate to
   */
  to: string;
  /**
   * If not providing the pathname to SidebarNav, you can manually set a nav item to be active
   */
  isActive?: boolean;
  /**
   * If you are providing a pathname to SidebarNav, this is the key it will attempt to match on.
   */
  activeKey?: string;
  /**
   * An optional onClick handler
   */
  onClick?: (e: MouseEvent) => void;
};

type PaidSidebarNavItemProps = SidebarNavItemProps & {
  /**
   * Whether or not the item is disabled and the pill should show.
   */
  isRestricted: boolean;
  /**
   * When provided, the nav item will still allow navigation on click, but will still gray out and show a pill.
   * Otherwise, it will display the upgrade modal.
   */
  allowNavigation?: boolean;
  /**
   * The feature to focus on when showing the plan selector modal.
   */
  modalFocusFeature?: FossaFeature;
};

type SidebarNavComponent = FunctionComponent<{ pathname?: string; }> & {
  Heading: FunctionComponent;
  Item: FunctionComponent<SidebarNavItemProps>;
  PaidItem: FunctionComponent<PaidSidebarNavItemProps>;
};

/**
 * SidebarNav is a set of navigation components which allows
 * you to build a vertically stacked navigation, which are typically
 * used in a SidebarNavLayout.
 *
 * An opt-in feature is to provide the `location.pathname` and an `activeKey`
 * prop to each item. This will use the last path segment to try to automatically
 * determine if the item is active or not. EG: activeKey="home" will match on
 * routes ending with "/home".
 *
 * @example
 * <SidebarNav>
 *   <SidebarNav.Heading>
 *     Settings
 *   </SidebarNav.Heading>
 *   <SidebarNav.Item to="/home" isActive>
 *     Home
 *   </SidebarNav.Item>
 *   <SidebarNav.Item to="/about">
 *     About
 *   </SidebarNav.Item>
 *   <SidebarNav.Item to="/contact">
 *     Contact
 *   </SidebarNav.Item>
 * </SidebarNav>
 */
const SidebarNav: SidebarNavComponent = ({ children, pathname }) => {
  const header = findFirstForSlot('heading', children);
  const items = findAllForSlot('item', children);
  return (
    <SidebarNavContextActiveKey.Provider value={useActiveKey(pathname)}>
      <nav
        className={styles.nav}
        aria-label="Sidebar Navigation"
      >
        {header}
        <ul
          className={styles.list}
          role="menubar"
          aria-label="Sidebar Navigation"
          aria-orientation="vertical"
        >
          {items}
        </ul>
      </nav>
    </SidebarNavContextActiveKey.Provider>
  );
};

SidebarNav.Heading = defineSlotComponent('heading', ({ children }) => (
  <h2 className={styles.heading}>
    {children}
  </h2>
));

SidebarNav.Item = defineSlotComponent('item', ({ children, to, isActive, activeKey, onClick }) => {
  const contextActiveKey = useSidebarNavActiveKey();
  const shouldUseContextToSetActive = !!contextActiveKey && !!activeKey;
  return (
    <li
      role="none"
      className={styles.item}
    >
      <RouterLink
        to={to}
        onClick={onClick}
        role="menuitem"
        //  Normally we wouldn't apply the className directly, but it's fine in this case
        className={cx(styles.itemLink, {
          //  isActive is the controlled prop, so it should take priority.
          [styles.itemLinkActive]: isActive || (shouldUseContextToSetActive && contextActiveKey === activeKey),
        })}
        tabIndex={0}
      >
        {children}
      </RouterLink>
    </li>
  );
});

SidebarNav.PaidItem = defineSlotComponent('item', ({ children, to, isActive, activeKey, onClick, isRestricted, allowNavigation, modalFocusFeature }) => {
  // If the item isn't restricted at all, just treat it normally.
  if (!isRestricted) {
    return (
      <SidebarNav.Item to={to} isActive={isActive} activeKey={activeKey} onClick={onClick}>
        {children}
      </SidebarNav.Item>
    );
  }

  // If the item is restricted, but we want to allow them to navigate to the page anyway, render it like normal but
  // add a PaidPill. (Exmaple: organization integration settings for Slack/Jira on a Free org)
  if (allowNavigation) {
    return (
      <SidebarNav.Item to={to} isActive={isActive} activeKey={activeKey} onClick={onClick}>
        <Flex justify="space-between">
          {children}
          <PaidPill />
        </Flex>
      </SidebarNav.Item>
    );
  }

  // If it's currently restricted and it shouldn't allow navigation, render it with a PaidFeatureGate to allow the upgrade
  // modal and add a PaidPill to the child content.
  return (
    <li
      role="none"
      className={styles.item}
    >
      <PaidFeatureGate isRestricted hidePill modalFocusFeature={modalFocusFeature}>
        <span className={styles.itemLink}>
          <Flex justify="space-between">
            {children}
            <PaidPill />
          </Flex>
        </span>
      </PaidFeatureGate>
    </li>
  );
});

export default SidebarNav;
